import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { S, formatCHF, formatDate } from '../regureStyles';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const TYPE_LABEL_KEY = {
  claims:     'screens.bordereaux.typeClaims',
  premium:    'screens.bordereaux.typePremium',
  combined:   'screens.bordereaux.typeCombined',
  loss_run:   'screens.bordereaux.typeLossRun',
  commission: 'screens.bordereaux.typeCommission',
};

const STATUS_LABEL_KEY = {
  ready:        'screens.bordereaux.statusReady',
  sent:         'screens.bordereaux.statusSent',
  generating:   'screens.bordereaux.statusGenerating',
  acknowledged: 'screens.bordereaux.statusAcknowledged',
  error:        'screens.bordereaux.statusError',
};

// D-29: no bordereaux detail view existed — clicking a row did nothing
// and the tester couldn't verify row count + premium totals. Same
// pattern as InvoiceDetailPanel: right-side sliding panel loads by _id
// via GET /bordereaux/:id (already registered in bordereauxRoutes.js).
const BordereauxDetailPanel = ({ bordereauxId, onClose }) => {
  const { t } = useTranslation();
  const [doc, setDoc] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!bordereauxId) return;
    let cancelled = false;
    setLoading(true);
    axios.get(`${BASE_URL}/bordereaux/${bordereauxId}`, { withCredentials: true })
      .then(resp => {
        if (cancelled) return;
        setDoc(resp.data?.data?.data || resp.data?.data || null);
      })
      .catch(err => {
        if (cancelled) return;
        setError(err.response?.data?.message || err.message || t('screens.bordereaux.loadFailed', { defaultValue: 'Failed to load bordereau' }));
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [bordereauxId, t]);

  const Row = ({ label, value, mono, valueStyle }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', padding: '10px 0', borderBottom: `1px solid ${S.borderLight}`, gap: 12 }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: S.textPrimary, fontFamily: mono ? 'monospace' : undefined, textAlign: 'right', ...(valueStyle || {}) }}>{value || '—'}</span>
    </div>
  );

  return (
    <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(15,23,42,0.35)', zIndex: 90 }} onClick={onClose}>
      <div
        onClick={e => e.stopPropagation()}
        style={{
          position: 'absolute',
          top: 0,
          right: 0,
          bottom: 0,
          width: 520,
          background: '#fff',
          boxShadow: '-10px 0 30px rgba(0,0,0,0.12)',
          padding: '28px 32px',
          overflowY: 'auto',
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.8 }}>
            {t('screens.bordereaux.detailTitle', { defaultValue: 'Bordereau Details' })}
          </div>
          <button type='button' onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 22, color: S.textMuted, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {loading && <div style={{ padding: 40, textAlign: 'center', color: S.textMuted, fontSize: 13 }}>{t('common.loading', { defaultValue: 'Loading…' })}</div>}
        {error && <div style={{ padding: 12, marginBottom: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{error}</div>}

        {!loading && doc && (() => {
          // D-29: the panel was reading top-level doc.premiumTotal / doc.currency
          // but the backend stores totals under `metadata.{totalGWP|totalReserves|
          // totalCommissions}` (see bordereauxModel.js). All three showed as
          // literal `???` on staging because typeof undefined === 'number' is
          // false → fell through to '—' AND the currency prefix was missing.
          // Pull from metadata + carrier and pick the right rollup by type.
          const meta = doc.metadata || {};
          // NOTE: formatCHF already prefixes the tenant symbol (CHF/USD/EUR)
          // based on window.REGURE_CURRENCY, so we no longer prepend the
          // currency variable to Row values below. Left the doc.currency
          // read commented so a future multi-currency-per-tenant patch
          // knows where to reintroduce it.
          // const currency = doc.currency || doc.carrier?.currency || 'CHF';
          const premiumTotal    = typeof meta.totalGWP === 'number' ? meta.totalGWP : null;
          const claimsTotal     = typeof meta.totalReserves === 'number' ? meta.totalReserves : null;
          const commissionTotal = typeof meta.totalCommissions === 'number' ? meta.totalCommissions : null;
          return (<>
            <div style={{ fontSize: 20, fontWeight: 700, color: S.textPrimary, marginBottom: 4 }}>{doc.carrier?.name || t('screens.bordereaux.unknownCarrier', { defaultValue: 'Unknown carrier' })}</div>
            <div style={{ fontSize: 12, color: S.textMuted, marginBottom: 18, fontFamily: 'monospace' }}>{doc._id}</div>

            <Row label={t('screens.bordereaux.colType', { defaultValue: 'Type' })} value={TYPE_LABEL_KEY[doc.type] ? t(TYPE_LABEL_KEY[doc.type]) : (doc.type || '—')} />
            <Row label={t('screens.bordereaux.colStatus', { defaultValue: 'Status' })} value={STATUS_LABEL_KEY[doc.status] ? t(STATUS_LABEL_KEY[doc.status]) : (doc.status || '—')} />
            <Row label={t('screens.bordereaux.colPeriod', { defaultValue: 'Period' })} value={`${doc.period?.from ? formatDate(doc.period.from) : '—'} → ${doc.period?.to ? formatDate(doc.period.to) : '—'}`} />
            <Row label={t('screens.bordereaux.colFormat', { defaultValue: 'Format' })} value={(doc.format || 'xlsx').toUpperCase()} mono />
            <Row label={t('screens.bordereaux.colRowCount', { defaultValue: 'Rows' })} value={doc.rowCount ?? '—'} />
            {premiumTotal !== null && (
              <Row
                label={t('screens.bordereaux.colPremiumTotal', { defaultValue: 'Premium total' })}
                value={formatCHF(premiumTotal)}
                valueStyle={{ color: S.burgundy }}
              />
            )}
            {claimsTotal !== null && (
              <Row label={t('screens.bordereaux.colClaimsTotal', { defaultValue: 'Claims total' })} value={formatCHF(claimsTotal)} />
            )}
            {commissionTotal !== null && (
              <Row label={t('screens.bordereaux.colCommissionTotal', { defaultValue: 'Commission total' })} value={formatCHF(commissionTotal)} />
            )}
            <Row label={t('screens.bordereaux.colFileSize', { defaultValue: 'File size' })} value={doc.fileSize ? `${Math.round(doc.fileSize / 1024)} KB` : '—'} />
            <Row label={t('screens.bordereaux.colGeneratedBy', { defaultValue: 'Generated by' })} value={doc.generatedBy?.name || '—'} />
            <Row label={t('screens.bordereaux.colGeneratedAt', { defaultValue: 'Generated at' })} value={doc.createdAt ? formatDate(doc.createdAt) : '—'} />
            {doc.sentAt && (
              <Row label={t('screens.bordereaux.colSentAt', { defaultValue: 'Sent at' })} value={formatDate(doc.sentAt)} />
            )}
            {doc.acknowledgedAt && (
              <Row label={t('screens.bordereaux.colAcknowledgedAt', { defaultValue: 'Acknowledged at' })} value={formatDate(doc.acknowledgedAt)} />
            )}

            {doc.status === 'error' && doc.errorMessage && (
              <div style={{ marginTop: 16, padding: 12, background: '#fef2f2', color: '#991b1b', borderRadius: 6, fontSize: 13 }}>{doc.errorMessage}</div>
            )}

            {Array.isArray(doc.validationWarnings) && doc.validationWarnings.length > 0 && (
              <div style={{ marginTop: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: S.textMuted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 6 }}>
                  {t('screens.bordereaux.validationWarnings', { defaultValue: 'Validation warnings' })}
                </div>
                {doc.validationWarnings.slice(0, 20).map((w, i) => (
                  <div key={i} style={{ fontSize: 12, color: S.textSecondary, padding: '4px 0', borderBottom: `1px solid ${S.borderLight}` }}>
                    • {typeof w === 'string' ? w : (w.message || JSON.stringify(w))}
                  </div>
                ))}
              </div>
            )}

            <div style={{ marginTop: 24, display: 'flex', gap: 10 }}>
              <button
                type='button'
                onClick={onClose}
                style={{ flex: 1, padding: '10px 14px', background: '#fff', color: S.textSecondary, border: `1px solid ${S.borderLight}`, borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}
              >
                {t('common.close', { defaultValue: 'Close' })}
              </button>
            </div>
          </>);
        })()}
      </div>
    </div>
  );
};

export default BordereauxDetailPanel;
